import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Search } from "lucide-react";
import { useProjectDesignationOptions } from "../../../Hooks/useProjectDesignationOptions.js";

import { formatCurrency } from "../../utils/currency.ts";
import { getDesignationTypeLabel, getDesignationName } from "../../utils/designation-display.ts";
import { phaseContributorCommercialRate, phaseContributorInternalRate, sortPhaseContributors, type PhaseContributorSortKey } from "./phase-contributor-display.ts";
import type { PhaseContributorTrackingReport, ProjectTrackingReport } from "./types.ts";
import styles from "./workstream-tracking-page.module.css";

type Props = {
  project: ProjectTrackingReport;
  currency: string;
};

type PhaseGroup = {
  key: string;
  name: string;
  code: string;
  color: string;
  rows: PhaseContributorTrackingReport[];
};

const number = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 1 });
const hoursText = (value: number) => `${number.format(value)}h`;
const phasePalette = ["#5968e9", "#8a45e9", "#d18a00", "#36a65c", "#456ee9", "#7d43e1"];

export function PhaseBreakdownCard({ project, currency }: Props) {
  const { options: designationOptions } = useProjectDesignationOptions();
  const phaseContributors = useMemo(() => project.phaseContributors ?? [], [project.phaseContributors]);
  const phaseContributorDataAvailable = Array.isArray(project.phaseContributors);
  const [query, setQuery] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [sortKey, setSortKey] = useState<PhaseContributorSortKey>("hours");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const phaseDefinitions = useMemo(() => project.phaseBreakdown ?? project.phases ?? [], [project.phaseBreakdown, project.phases]);
  const initialPhaseKey = String(phaseDefinitions[0]?.phaseId ?? phaseDefinitions[0]?.phaseName ?? phaseDefinitions[0]?.phase ?? "");
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(initialPhaseKey ? [initialPhaseKey] : []));

  const groups = useMemo<PhaseGroup[]>(() => {
    const needle = query.trim().toLowerCase();
    return phaseDefinitions.map((phase, index) => {
      const key = String(phase.phaseId ?? phase.phaseName ?? phase.phase ?? index);
      const name = phase.phaseName || phase.phase || `Phase ${index + 1}`;
      const code = phase.phase || phase.phaseName || "";
      const phaseMatches = !needle || `${name} ${code}`.toLowerCase().includes(needle);
      const rows = sortPhaseContributors(phaseContributors
        .filter((item) => item.phaseId === phase.phaseId || (!phase.phaseId && item.phaseName === name))
        .filter((item) => phaseMatches || `${item.employeeName} ${getDesignationName(item.designation)} ${item.projectExpertise ?? ""} ${getDesignationTypeLabel(item.designation, item.locationType)} ${membershipLabel(item.memberCategory)}`.toLowerCase().includes(needle)), sortKey, sortDirection);
      return { key, name, code, rows, color: phasePalette[index % phasePalette.length] };
    }).filter((group) => !needle || group.rows.length > 0 || `${group.name} ${group.code}`.toLowerCase().includes(needle));
  }, [phaseContributors, phaseDefinitions, query, sortDirection, sortKey, designationOptions]);

  const totals = useMemo(() => groups.reduce((total, group) => {
    group.rows.forEach((item) => {
      total.hours += Number(item.hours ?? 0);
      total.burn += Number(item.commercialBurn ?? 0);
      total.cost += Number(item.internalCost ?? 0);
    });
    return total;
  }, { hours: 0, burn: 0, cost: 0 }), [groups]);
  const visibleGroups = activeOnly
    ? groups.filter((group) => group.rows.some((item) => item.memberCategory === "ACTIVE"))
    : groups;

  const changeSort = (nextKey: PhaseContributorSortKey) => {
    if (sortKey === nextKey) setSortDirection((current) => current === "desc" ? "asc" : "desc");
    else { setSortKey(nextKey); setSortDirection("desc"); }
  };
  const toggle = (key: string) => setExpanded((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  return <section className={styles.phaseBreakdownCard}>
    <header className={styles.phaseBreakdownHeader}>
      <div><h2>Phase-Wise Breakdown</h2><p>Phase · Designation · Employee · Expertise · Type · Billing Rate · Caelius Cost · Hours · Burn · Cost</p></div>
      <div className={styles.phaseBreakdownControls}>
        <button type="button" className={styles.phaseBreakdownActiveToggle} aria-pressed={activeOnly} onClick={() => setActiveOnly((current) => !current)}><span aria-hidden="true" />Only active</button>
        <label className={styles.phaseBreakdownSearch}><Search /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search phase, role or name..." aria-label="Search phase breakdown" /></label>
      </div>
    </header>
    {!phaseContributorDataAvailable && <div className={styles.phaseBreakdownNotice}>Phase contributor details are not present in the current report response. Restart the updated backend service to load employee-wise phase entries.</div>}
    <div className={styles.phaseBreakdownColumns}><span>Phase</span><span>Designation</span><span>Employee</span><span>Expertise</span><span>Type</span><SortHeading label="Billing Rate" column="commercialRate" active={sortKey} direction={sortDirection} onSort={changeSort} /><SortHeading label="Caelius Cost" column="internalRate" active={sortKey} direction={sortDirection} onSort={changeSort} /><SortHeading label="Hours" column="hours" active={sortKey} direction={sortDirection} onSort={changeSort} /><SortHeading label="Burn" column="commercialBurn" active={sortKey} direction={sortDirection} onSort={changeSort} /><SortHeading label="Cost" column="internalCost" active={sortKey} direction={sortDirection} onSort={changeSort} /></div>
    <div className={styles.phaseBreakdownGroups}>
      {visibleGroups.length ? visibleGroups.map((group) => {
        const isExpanded = query.trim() ? true : expanded.has(group.key);
        const visibleRows = activeOnly ? group.rows.filter((item) => item.memberCategory === "ACTIVE") : group.rows;
        const phaseHours = group.rows.reduce((sum, item) => sum + Number(item.hours ?? 0), 0);
        const phaseBurn = group.rows.reduce((sum, item) => sum + Number(item.commercialBurn ?? 0), 0);
        const phaseCost = group.rows.reduce((sum, item) => sum + Number(item.internalCost ?? 0), 0);
        return <div className={styles.phaseBreakdownGroup} key={group.key}>
          <button type="button" className={styles.phaseBreakdownGroupHeader} onClick={() => toggle(group.key)} aria-expanded={isExpanded}>
            <span>{isExpanded ? <ChevronDown /> : <ChevronRight />}<i style={{ background: group.color }} /><strong>{group.name}</strong><small>{activeOnly ? `${visibleRows.length} active of ${group.rows.length}` : `${group.rows.length} ${group.rows.length === 1 ? "entry" : "entries"}`}</small></span>
            <b>{hoursText(phaseHours)}</b><b>{formatCurrency(phaseBurn, currency, 0)}</b><b>{formatCurrency(phaseCost, currency, 0)}</b>
          </button>
          {isExpanded && <div className={styles.phaseBreakdownRows}>{visibleRows.length ? visibleRows.map((item) => <div className={styles.phaseBreakdownRow} key={`${group.key}-${item.employeeId}`}>
            <span className={styles.phaseTreeLine} />
            <span className={styles.phaseDesignation} title={getDesignationName(item.designation)}><i style={{ background: group.color }} />{getDesignationName(item.designation)}</span>
            <span className={styles.phaseEmployee}><i>{initials(item.employeeName)}</i><span className={styles.phaseEmployeeName} title={item.employeeName}>{displayEmployeeName(item.employeeName)}</span><MembershipBadge category={item.memberCategory} /></span>
            <span className={styles.phaseExpertise} title={item.projectExpertise ?? ""}>{item.projectExpertise?.trim() || "—"}</span>
            <span className={styles.phaseType}>{getDesignationTypeLabel(item.designation, item.locationType)}</span>
            <span className={styles.phaseRate} title={item.commercialRateOverride ? "Member commercial-rate override" : "Project designation rate card"}>
              {phaseContributorCommercialRate(item) == null ? "—" : formatCurrency(phaseContributorCommercialRate(item), currency)}
              {item.commercialRateOverride && <small>Member rate</small>}
            </span>
            <span className={styles.phaseRate}>{phaseContributorInternalRate(item) == null ? "—" : formatCurrency(phaseContributorInternalRate(item), "USD")}</span>
            <span className={styles.phaseHours}><b>{hoursText(Number(item.hours ?? 0))}</b></span>
            <strong className={styles.phaseBurn}>{item.commercialBurn == null ? "—" : formatCurrency(item.commercialBurn, currency, 0)}</strong>
            <strong className={styles.phaseCost}>{item.internalCost == null ? "—" : formatCurrency(item.internalCost, currency, 0)}</strong>
          </div>) : <div className={styles.phaseBreakdownEmpty}>No approved contributor hours in this phase.</div>}</div>}
        </div>;
      }) : <div className={styles.phaseBreakdownEmpty}>{activeOnly ? "No active contributors match your search." : "No phase breakdown matches your search."}</div>}
    </div>
    <footer className={styles.phaseBreakdownTotal}><strong>{activeOnly ? "Total (all members)" : "Total"}</strong><b>{hoursText(totals.hours)}</b><b>{formatCurrency(totals.burn, currency, 0)}</b><b>{formatCurrency(totals.cost, currency, 0)}</b></footer>
  </section>;
}

function SortHeading({ label, column, active, direction, onSort }: { label: string; column: PhaseContributorSortKey; active: PhaseContributorSortKey; direction: "asc" | "desc"; onSort: (key: PhaseContributorSortKey) => void }) {
  return <button type="button" onClick={() => onSort(column)}>{label}{active === column ? direction === "desc" ? <ArrowDown /> : <ArrowUp /> : <ArrowUp />}</button>;
}

function initials(name: string) {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return (words.length > 1 ? `${words[0][0]}${words.at(-1)?.[0] ?? ""}` : words[0]?.slice(0, 2) || "?").toUpperCase();
}

function displayEmployeeName(name: string) {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return words.length > 1 ? `${words[0]} ${words.at(-1)}` : name;
}

function membershipLabel(category: PhaseContributorTrackingReport["memberCategory"]) {
  if (category === "ACTIVE") return "Active";
  if (category === "SHADOW") return "Shadow";
  if (category === "DEALLOCATED") return "Deallocated";
  return "";
}

function MembershipBadge({ category }: { category: PhaseContributorTrackingReport["memberCategory"] }) {
  const label = membershipLabel(category);
  if (!label) return null;
  const tone = category === "ACTIVE" ? styles.membershipActive : category === "SHADOW" ? styles.membershipShadow : styles.membershipDeallocated;
  return <small className={`${styles.membershipBadge} ${tone}`}>{label}</small>;
}
